import { useCallback, memo, useMemo, useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import {
  User as UserIcon,
  FavoriteOutlined as FavoriteIcon,
  Lock as LockIcon,
  Unlock as UnlockIcon,
  Mail as MailIcon,
  MessageCircle as MessageCircleIcon,
  Magazine as MagazineIcon,
  SettingsOutlined as SettingsOutlinedIcon,
} from '@vgno/icons';

import { config } from '../../../config';
import { useAccessContext } from '../../AccessProvider/AccessProvider';
import type { MenuItem } from '../helpers';
import { getPath, routeNames } from '../../../shared/helpers/routes';
import { useIdentity } from '../../IdentityProvider/IdentityProvider';
import { useParentalControls } from '../../ParentalControl/shared/useParentalControls';
import { parentalControlsHelper } from '../../../helpers/parentalControls';
import { getCurrentPageViewData } from '../../../services/tracking/helpers/pulseOrigin';
import { trackButtonClickEvent } from '../../../services/tracking/pulseEvents';
import { isTruthy } from '../../../utils/types';
import { hasValidSubscriptionAccess } from '../../../shared/helpers/subscriptionHelpers';
import { Overlay } from '../../Overlay/Overlay';
import { useViewportType } from '../../utils/useViewportType';
import { consentManager } from '../../../services/privacy';
import { ReactComponent as BigScreenIcon } from '../../../icons/tv-regular.svg';
import { getTracker } from '../../../services/tracking/pulse/pulse';

import { UserMenuBase } from './UserMenuBase';

import * as styles from './UserMenu.module.scss';

type Props = {
  isOpen: boolean;
  dismiss?: () => void;
};

const utmParams = {
  utm_source: config.provider === 'ab' ? 'abtv' : 'vgtv',
  utm_medium: 'usermenu',
};

export const UserMenu = memo(({ isOpen, dismiss }: Props) => {
  const { t } = useTranslation();
  const access = useAccessContext();
  const hasSubscription = hasValidSubscriptionAccess(access.accesses);
  const { isLoggedIn, fullName } = useIdentity();
  const clientId = config.schibstedAccount.clientId;

  const { isMobile } = useViewportType();

  const [envId, setEnvId] = useState<string | undefined>(undefined);

  useEffect(() => {
    getTracker()
      .then((tracker) => tracker.getEnvironmentId())
      .then(setEnvId)
      .catch((err) => console.error(err));
  }, []);

  // PARENTAL CONTROL
  const { showParentalControlsManager } = useParentalControls();
  const parentalControlsLabel = parentalControlsHelper.isParentalLockSet()
    ? t('deactivate parental controls')
    : t('activate parental controls');

  // TODO: is this reqiured since we have navigation tracking?
  const trackParentalControlsClick = useCallback((label: string) => {
    const currentPageViewData = getCurrentPageViewData();

    const button = {
      elementType: 'ParentalControlButton',
      title: label,
    };

    const targetUrl = currentPageViewData.url;

    const target = {
      elementType: parentalControlsHelper.isParentalLockSet()
        ? 'DisableParentalControls'
        : 'ActivateParentalControls',
      name: 'ParentalControls',
      url: targetUrl,
    };
    trackButtonClickEvent(button, target);
  }, []);
  // END PARENTAL CONTROLS

  // on mount - wait until account is ready, and populate menu item elements for the current user state
  const menuItems = useMemo(() => {
    let items: { items: MenuItem[]; label?: string }[] = [];

    if (config.provider === 'ab') {
      items.push({
        items: [
          { label: 'Kundsupport', link: config.supportPageUrl },
          {
            label: 'Kontoinställningar',
            link: config.schibstedAccount.myPageUrl,
            disabled: !isLoggedIn,
          },
          {
            label: 'Datainställningar',
            link: config.schibstedAccount.myPageUrl,
            disabled: !isLoggedIn,
          },
          consentManager.type !== 'fallback' && {
            label: 'Inställningar för cookies',
            onClick: () => consentManager.showPrivacyManager(),
          },
        ].filter(isTruthy),
      });
    } else {
      items.push({
        items: [
          config.enableFavourites && {
            label: t('My list'),
            disabled: !isLoggedIn,
            icon: <FavoriteIcon />,
            link: getPath(routeNames.favourites),
          },
          config.enableParentalControls && {
            label: parentalControlsLabel,
            icon: parentalControlsHelper.isParentalLockSet() ? (
              <LockIcon />
            ) : (
              <UnlockIcon />
            ),
            onClick() {
              trackParentalControlsClick(parentalControlsLabel);
              showParentalControlsManager();
            },
          },
          {
            label: t('VGTV on the big screen'),
            icon: <BigScreenIcon />,
            link: getPath(routeNames.bigScreen),
          },
        ].filter(isTruthy),
      });

      items.push({
        items: [
          {
            label: t('My page'),
            icon: <UserIcon />,
            disabled: !isLoggedIn,
            link: config.schibstedAccount.myPageUrl,
          },
          {
            label: hasSubscription
              ? t('My subscription')
              : t('Buy subscription'),
            icon: <MagazineIcon />,
            link: config.mySubscriptionUrl,
          },
          {
            label: t('Settings'),
            disabled: !isLoggedIn,
            icon: <SettingsOutlinedIcon />,
            link: config.newsletterUrl,
          },
          {
            label: t('Newsletter'),
            disabled: !isLoggedIn,
            icon: <MailIcon />,
            link: config.newsletterUrl,
          },
          {
            label: t('Support'),
            icon: <MessageCircleIcon />,
            link: config.supportPageUrl,
          },
        ],
      });

      const privacyUrl = `https://privacysettings.schibsted.no/?client_id=${clientId}${envId ? `&environment_id=${envId}` : ''}`;

      items.push({
        label: t('Schibsted account'),
        items: [
          {
            label: t('My Schibsted account'),
            disabled: !isLoggedIn,
            link: `https://payment.schibsted.no/account/summary?client_id=${clientId}`,
          },
          {
            label: t('Privacy settings'),
            link: privacyUrl,
          },
          consentManager.type !== 'fallback' && {
            label: t('Consent settings'),
            onClick: () => consentManager.showPrivacyManager(),
          },
        ].filter(isTruthy),
      });
    }

    return items.filter(({ items }) => items.length > 0);
  }, [
    clientId,
    t,
    isLoggedIn,
    hasSubscription,
    parentalControlsLabel,
    showParentalControlsManager,
    trackParentalControlsClick,
    envId,
  ]);

  return (
    <Overlay
      onBackdropClick={dismiss}
      className={styles.overlay}
      type="overlay/rightDrawer"
      isOpen={isOpen}
      disableScrollLock={!isMobile}
    >
      <UserMenuBase
        accesses={access.accesses}
        fullName={fullName}
        menuItems={menuItems}
        onMenuItemClick={dismiss}
        utmParams={utmParams}
      />
    </Overlay>
  );
});
